import React from "react";
import moment from "moment";
import {
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

import colors from "../../helpers/colors";
import useMyaThreadTranscript, {
  MYA_TRANSCRIPT_ERRORS,
} from "../../hooks/useMyaThreadTranscript";
import { LoadingIndicator } from "../LoadingIndicator";
import { isMoblyzeAdmin } from "../../helpers/permissions";

// Moblyze staff only (see helpers/permissions). The API only resolves
// ownerMyaThreadId for them or the job's owner — this check is a second layer
// so nobody else sees the section or fires the fetch. Re-exported for callers
// that imported it from here.
export { isMoblyzeAdmin };

const OUTPUT_PREVIEW_CHARS = 300;

function formatTimestamp(value) {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.format("D MMM YYYY, HH:mm:ss") : null;
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

// One line for a block that isn't a chat message, so an admin can follow what
// the owner was shown without reading raw card payloads.
export function summarizeBlock(block) {
  switch (block.kind) {
    case "proposal":
      return `Proposal: ${block.title || block.tool || "Untitled"} — ${block.status || "unknown"}`;
    case "role_draft":
      return `Role draft: ${block.draft?.title || "Untitled role"}`;
    case "criteria": {
      const n = block.criteria?.length ?? 0;
      return `Criteria: ${n} ${n === 1 ? "criterion" : "criteria"}`;
    }
    case "candidate_list":
      return `Candidate list: ${block.title || "Candidates"} (${plural(block.candidates?.length ?? 0, "candidate")})`;
    case "job_list":
      return `Job list: ${block.title || "Jobs"} (${plural(block.jobs?.length ?? 0, "job")})`;
    case "actions": {
      const labels = (block.actions || []).map((a) => a?.label).filter(Boolean);
      return `Actions: ${labels.length ? labels.join(", ") : "none"}`;
    }
    case "email_draft":
      return `Email draft: ${block.subject || "(no subject)"}${block.to ? ` to ${block.to}` : ""}`;
    case "result":
      return `Result: ${block.ok ? "succeeded" : "failed"}${block.text ? ` — ${block.text}` : ""}`;
    case "error":
      return `Error: ${block.text || "unknown error"}`;
    case "job_event":
      return `Job event: ${block.text || jobEventLabel(block.eventType)}`;
    default:
      return `${block.kind || "Unknown"} block`;
  }
}

// "matching_completed" -> "Matching completed"; fallback when there are no
// words.
function humanize(value, fallback) {
  const words = String(value || "").replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : fallback;
}

// The worker's eventType is the MCP's snake_case type, and more types may
// come.
export function jobEventLabel(eventType) {
  return humanize(eventType, "Job event");
}

// A card for something in the thread that is neither the owner's nor Mya's
// words: a job event or a tool result. Tagged and coloured by what it is, so
// it reads apart from the chat bubbles and the one-line summaries.
function EventCard({ testId, tag, accent, tint, title, text, details }) {
  return (
    <Box
      data-testid={testId}
      borderLeft="3px solid"
      borderColor={accent}
      bg={tint}
      borderRadius="6px"
      p="8px 12px"
    >
      <Flex gap="8px" align="center" wrap="wrap" mb="4px">
        <Badge bg={accent} color="white" fontSize="2xs">
          {tag}
        </Badge>
        <Text fontSize="xs" fontWeight="bold" color="white">
          {title}
        </Text>
      </Flex>
      {text && (
        <Text fontSize="sm" color="white" whiteSpace="pre-wrap" wordBreak="break-word">
          {text}
        </Text>
      )}
      {details.length > 0 && (
        <Text fontSize="xs" color="gray.400" mt="2px">
          {details.join(" · ")}
        </Text>
      )}
    </Box>
  );
}

// Something that happened to the job (matching started or finished), which
// the worker writes into the thread between turns. Its time is when it
// happened (occurredAt), not when the thread learned of it.
function JobEventBlock({ block }) {
  const timestamp = formatTimestamp(block.occurredAt) || formatTimestamp(block.createdAt);
  return (
    <EventCard
      testId="mya-transcript-job-event"
      tag="Job event"
      accent={colors.brandBlue}
      tint={colors.lightBlueBg}
      title={jobEventLabel(block.eventType)}
      text={block.text}
      details={[
        block.jobName,
        block.jobVersion != null ? `v${block.jobVersion}` : null,
        timestamp,
      ].filter(Boolean)}
    />
  );
}

// "moblyze_triage_candidate" -> "Triage candidate".
export function toolLabel(tool) {
  return humanize(String(tool || "").replace(/^moblyze_/, ""), "Tool");
}

// How a tool call ended. A pre-approved call still in flight is "running"
// until the worker replaces the block with its outcome.
export function resultStatus(block) {
  if (block.running) return "Running";
  return block.ok ? "Succeeded" : "Failed";
}

const RESULT_COLORS = {
  Succeeded: { accent: colors.brandGreen, tint: colors.brandGreenBg },
  Failed: { accent: colors.danger, tint: colors.dangerBg },
  Running: { accent: colors.warning, tint: colors.warningBg },
};

// The outcome of a tool Mya called for the owner (a confirmed proposal, or a
// recommendation pre-approved for the automatic job run). The block doesn't
// name the tool; its proposal does.
function ToolResultBlock({ block, proposal }) {
  const status = resultStatus(block);
  const { accent, tint } = RESULT_COLORS[status];
  return (
    <EventCard
      testId="mya-transcript-tool-result"
      tag="Tool result"
      accent={accent}
      tint={tint}
      title={proposal?.tool ? `${toolLabel(proposal.tool)} — ${status}` : status}
      text={block.text}
      details={[
        block.preApproved ? "Pre-approved" : null,
        formatTimestamp(block.createdAt),
      ].filter(Boolean)}
    />
  );
}

function ownerLabel(meta) {
  return meta?.role ? `Owner (${meta.role})` : "Owner";
}

function ChatBlock({ label, text, timestamp, isOwner }) {
  return (
    <Flex direction="column" align={isOwner ? "flex-end" : "flex-start"}>
      <Flex gap="8px" align="baseline" mb="4px">
        <Text fontSize="xs" fontWeight="bold" color="white">
          {label}
        </Text>
        {timestamp && (
          <Text fontSize="xs" color="gray.400">
            {timestamp}
          </Text>
        )}
      </Flex>
      <Box
        maxWidth="85%"
        bg={isOwner ? colors.brandGreen : colors.greyMedium}
        borderRadius="12px"
        p="8px 12px"
      >
        <Text fontSize="sm" color="white" whiteSpace="pre-wrap" wordBreak="break-word">
          {text}
        </Text>
      </Box>
    </Flex>
  );
}

function BlocksView({ blocks, meta, proposals }) {
  const proposalsById = new Map((proposals || []).map((p) => [p.id, p]));
  const visible = (blocks || []).filter((b) => b && b.kind !== "status");
  if (visible.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400">
        This thread has no messages yet.
      </Text>
    );
  }

  return (
    <Stack spacing="16px" data-testid="mya-transcript-blocks">
      {visible.map((block) => {
        const timestamp = formatTimestamp(block.createdAt);
        if (block.kind === "user") {
          return (
            <ChatBlock
              key={block.id}
              label={ownerLabel(meta)}
              text={block.text}
              timestamp={timestamp}
              isOwner
            />
          );
        }
        if (block.kind === "text") {
          return (
            <ChatBlock key={block.id} label="Mya" text={block.text} timestamp={timestamp} />
          );
        }
        if (block.kind === "job_event") {
          return <JobEventBlock key={block.id} block={block} />;
        }
        if (block.kind === "result") {
          return (
            <ToolResultBlock
              key={block.id}
              block={block}
              proposal={proposalsById.get(block.proposalId)}
            />
          );
        }
        return (
          <Flex key={block.id} gap="8px" align="baseline" wrap="wrap">
            <Text fontSize="sm" color="gray.300" fontStyle="italic">
              {summarizeBlock(block)}
            </Text>
            {timestamp && (
              <Text fontSize="xs" color="gray.500">
                {timestamp}
              </Text>
            )}
          </Flex>
        );
      })}
    </Stack>
  );
}

function messageText(content) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((part) => (typeof part === "string" ? part : part?.text))
    .filter(Boolean)
    .join("\n");
}

function prettyArguments(args) {
  if (args == null) return "";
  if (typeof args !== "string") return JSON.stringify(args, null, 2);
  try {
    return JSON.stringify(JSON.parse(args), null, 2);
  } catch {
    return args;
  }
}

function PreBlock({ children }) {
  return (
    <Code
      display="block"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
      bg={colors.black}
      color="gray.200"
      p="8px"
      fontSize="xs"
      borderRadius="4px"
    >
      {children}
    </Code>
  );
}

function FunctionOutput({ output }) {
  const [expanded, setExpanded] = React.useState(false);
  const text = typeof output === "string" ? output : JSON.stringify(output, null, 2) ?? "";
  const isLong = text.length > OUTPUT_PREVIEW_CHARS;
  return (
    <Box>
      <PreBlock>
        {isLong && !expanded ? `${text.slice(0, OUTPUT_PREVIEW_CHARS)}…` : text}
      </PreBlock>
      {isLong && (
        <Button size="xs" variant="link" color="gray.300" mt="4px" onClick={() => setExpanded((e) => !e)}>
          {expanded ? "Show less" : `Show full output (${text.length} chars)`}
        </Button>
      )}
    </Box>
  );
}

function RawItem({ item }) {
  let label;
  let body;
  if (item.type === "message" || (!item.type && item.role)) {
    label = `${item.role || "unknown"} message`;
    body = (
      <Text fontSize="sm" color="white" whiteSpace="pre-wrap" wordBreak="break-word">
        {messageText(item.content)}
      </Text>
    );
  } else if (item.type === "function_call") {
    label = `function_call: ${item.name || "unknown"}`;
    body = <PreBlock>{prettyArguments(item.arguments)}</PreBlock>;
  } else if (item.type === "function_call_output") {
    label = `function_call_output${item.call_id ? ` (${item.call_id})` : ""}`;
    body = <FunctionOutput output={item.output} />;
  } else {
    label = item.type || "item";
    const { seq, ...rest } = item;
    body = <FunctionOutput output={rest} />;
  }

  return (
    <Box borderLeft="2px solid" borderColor={colors.greyBorder} pl="10px">
      <Flex gap="8px" align="center" mb="4px">
        <Badge fontSize="9px">#{item.seq}</Badge>
        <Text fontSize="xs" fontWeight="bold" color="gray.300">
          {label}
        </Text>
      </Flex>
      {body}
    </Box>
  );
}

function RawTranscriptView({ transcript }) {
  const items = transcript || [];
  if (items.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400">
        The raw transcript is empty.
      </Text>
    );
  }
  return (
    <Stack spacing="12px" data-testid="mya-transcript-raw">
      {items.map((item, i) => (
        <RawItem key={item.seq ?? i} item={item} />
      ))}
    </Stack>
  );
}

function errorText(error) {
  if (error.code === MYA_TRANSCRIPT_ERRORS.FORBIDDEN) {
    return "You don't have access to this Client Mya transcript.";
  }
  if (error.code === MYA_TRANSCRIPT_ERRORS.NOT_FOUND) {
    return "Thread not found.";
  }
  return error.message || "Failed to load the Client Mya transcript.";
}

function TranscriptBody({ data, loading, error }) {
  const [showRaw, setShowRaw] = React.useState(false);

  if (loading) return <LoadingIndicator />;
  if (error) {
    return (
      <Text fontSize="sm" color={colors.danger} role="alert">
        {errorText(error)}
      </Text>
    );
  }
  if (!data) return null;

  return (
    <Stack spacing="16px">
      <Flex align="center" gap="8px">
        <Switch
          id="mya-transcript-show-raw"
          isChecked={showRaw}
          onChange={(e) => setShowRaw(e.target.checked)}
        />
        <Text as="label" htmlFor="mya-transcript-show-raw" fontSize="sm" color="gray.300">
          Show raw transcript (tool calls)
        </Text>
      </Flex>
      {showRaw ? (
        <RawTranscriptView transcript={data.transcript} />
      ) : (
        <BlocksView blocks={data.blocks} meta={data.meta} proposals={data.proposals} />
      )}
    </Stack>
  );
}

// Moblyze-admin-only: the chat transcript of the Mya thread that owns this job.
// Fetched lazily, the first time the admin opens it.
const MyaThreadTranscriptSection = ({ moblyzeJob, currentUser }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [hasOpened, setHasOpened] = React.useState(false);
  const isAdmin = isMoblyzeAdmin(currentUser);
  const threadId = isAdmin ? moblyzeJob?.ownerMyaThreadId : null;

  const { data, loading, error } = useMyaThreadTranscript(threadId, isAdmin && hasOpened);

  if (!isAdmin) return null;

  const handleOpen = () => {
    setHasOpened(true);
    onOpen();
  };

  return (
    <Flex align="center" gap="12px" data-testid="mya-transcript-section">
      <Text fontSize="sm" color="gray.400">
        Admin:
      </Text>
      {threadId ? (
        <Button size="sm" variant="outline" onClick={handleOpen}>
          View Client Mya Transcript
        </Button>
      ) : (
        <Text fontSize="sm" color="gray.400">
          This job has no Client Mya thread yet.
        </Text>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside" variant="darkMode">
        <ModalOverlay />
        <ModalContent bg={colors.greyDark} color="white">
          <ModalHeader>
            Client Mya Transcript
            {data?.meta?.title || data?.thread?.title ? (
              <Text fontSize="sm" fontWeight="normal" color="gray.400">
                {data.meta?.title || data.thread?.title}
              </Text>
            ) : null}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="24px">
            <TranscriptBody data={data} loading={loading} error={error} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default MyaThreadTranscriptSection;
